import ActionTextIcon from "@/components/icons/ActionTextIcon";
import Ellipsis from "@/components/views/Ellipsis";
import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleDateTime } from "@/utils/formatUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero } from "@/utils/utils";
import { App as AntApp, Divider, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { Fragment, useMemo, useState } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useFilterCalcRecords } from "../../../../../common/api/queries";
import { QueryKeys, TableSizes } from "../../../../../common/constants";
import { CalcType } from "../../../enums";
import { CALC_ROUTE_PATHS } from "../../../paths";
import { CalcRecordFilterPageRequest, CalcRecordList } from "../../types";
import { getCalcRecordFilterPath } from "../../utils";
import { CalcTypeTag } from "../CalcTypeTag";
import { CalcRecordRequestResponseLogsModal } from "../modals/CalcRecordRequestResponseLogsModal";
import { CalcRecordStackTracesLogsModal } from "../modals/CalcRecordStackTracesLogsModal";
import { CalcRecordWebservicesLogsModal } from "../modals/CalcRecordWebservicesLogsModal";
import { OperationTypeTag } from "../OperationTypeTag";

interface Props {
  searchParams: CalcRecordFilterPageRequest;
}

export const CalcRecordsListTableView = ({ searchParams }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { message } = AntApp.useApp();

  const [requestResponseLogsId, setRequestResponseLogsId] = useState<string>();
  const [webserviceLogsId, setWebserviceLogsId] = useState<string>();
  const [stackTraceLogsId, setStackTraceLogsId] = useState<string>();

  const { data: calcRecordsPage } = useFilterCalcRecords(searchParams);

  const columns: ColumnsType<CalcRecordList> = useMemo(
    () => [
      {
        key: "createdAt",
        title: t("common.createdAt"),
        width: 160,
        render: (_, record) => formatLocaleDateTime(record.createdAt),
      },
      {
        key: "account",
        title: t("calc.records.attrs.userId"),
        width: 260,
        ellipsis: { showTitle: false },
        render: (_, record) => <Ellipsis>{record.user.name}</Ellipsis>,
      },
      {
        key: "operationType",
        title: t("calc.records.enums.operationType._label"),
        width: 150,
        render: (_, record) => <OperationTypeTag type={record.operationType} style={{ marginRight: 0 }} />,
      },
      {
        key: "calcType",
        title: t("calc.enums.calcType._label"),
        width: 140,
        render: (_, record) => <CalcTypeTag type={record.calcType} style={{ marginRight: 0 }} />,
      },
      {
        key: "actions",
        align: "right",
        fixed: "right",
        width: 440,
        render: (_, record) => {
          let calcPath = "";

          switch (record.calcType) {
            case CalcType.REALTY:
              calcPath = CALC_ROUTE_PATHS.realty.to;
              break;
            case CalcType.TRAVEL:
              calcPath = CALC_ROUTE_PATHS.travel.to;
              break;
            case CalcType.MTPL:
            case CalcType.CRASH:
            case CalcType.MTPL_CRASH:
            case CalcType.GAP:
            case CalcType.PAS:
              calcPath = CALC_ROUTE_PATHS.vehicle.to;
              break;
          }

          const calcRecordPath = `${calcPath}${URLQuery.searchParamsToString({ searchParams: { [QueryKeys.RECORD_ID]: record.id } })}`;

          return (
            <Fragment key={record.id}>
              <ActionTextIcon
                icon="laptop"
                color="blue"
                text={t("calc.records.actions.showLogs")}
                onClick={() => setRequestResponseLogsId(record.id)}
              />
              <Divider orientation="vertical" />

              <ActionTextIcon
                icon="cloud"
                color="blue"
                text={t("calc.records.actions.showWebserviceLogs")}
                disabled={!record.hasWebservicesLogs}
                onClick={() => setWebserviceLogsId(record.id)}
              />
              <Divider orientation="vertical" />

              <ActionTextIcon
                icon="exclamation"
                color="orange"
                text={t("calc.records.actions.showStackTraceLogs")}
                disabled={!record.hasStackTracesLogs}
                onClick={() => setStackTraceLogsId(record.id)}
              />
              <Divider orientation="vertical" />

              <CopyToClipboard
                text={window.location.origin + getCalcRecordFilterPath(record)}
                onCopy={() => message.info(t("common.clipboardCopied"))}
              >
                <ActionTextIcon icon="link" color="blue" text={t("calc.records.actions.copyLink")} />
              </CopyToClipboard>
              <Divider orientation="vertical" />

              <ActionTextIcon
                path={calcRecordPath}
                target="_blank"
                icon="select"
                color="green"
                text={t("calc.records.actions.showInCalcForm")}
              />
            </Fragment>
          );
        },
      },
    ],
    [],
  );

  return (
    <>
      <Table<CalcRecordList>
        {...tableStandardProps()}
        columns={columns}
        scroll={{ x: TableSizes.LARGE }}
        dataSource={calcRecordsPage?.pageData}
        pagination={{
          ...paginationTableProps,
          current: numberOrZero(calcRecordsPage?.pageIndex) + 1,
          pageSize: calcRecordsPage?.pageSize,
          total: calcRecordsPage?.totalElementsCount,
          onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
        }}
      />

      <CalcRecordRequestResponseLogsModal
        id={requestResponseLogsId}
        onCancelClick={() => setRequestResponseLogsId(undefined)}
      />

      <CalcRecordWebservicesLogsModal id={webserviceLogsId} onCancelClick={() => setWebserviceLogsId(undefined)} />

      <CalcRecordStackTracesLogsModal id={stackTraceLogsId} onCancelClick={() => setStackTraceLogsId(undefined)} />
    </>
  );
};
